import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { useT } from '../i18n/index.ts';
import { inRange, monthGrid, parseIso, shiftDays, shiftMonths, toIso } from '../lib/calendar.ts';
import { latestDate } from '../lib/dates.ts';
import { Icon } from '../ui/icons.tsx';

/** Month and weekday names in the app's language (Western digits in both). */
function names(lang: 'en' | 'ur') {
  const locale = lang === 'ur' ? 'ur-PK-u-nu-latn' : 'en-GB';
  const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: lang === 'ur' ? 'long' : 'short', timeZone: 'UTC' });
  const full = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  // 2024-01-01 was a Monday: Monday-first weekday headings (RULES.md rule 9).
  const weekdays = Array.from({ length: 7 }, (_, i) => weekday.format(new Date(Date.UTC(2024, 0, 1 + i))));
  return {
    monthTitle: (year: number, m: number) => month.format(new Date(Date.UTC(year, m, 15))),
    // Urdu day names don't shorten well, so they stay whole (in a smaller size).
    weekdays: lang === 'ur' ? weekdays : weekdays.map((w) => w.slice(0, 2)),
    fullDay: (iso: string) => full.format(new Date(`${iso}T12:00:00Z`)),
  };
}

/**
 * "Other date": a month calendar for any earlier day (owner's decision, 8 Oct 2026) or up to
 * 7 days ahead. Day cells are at least 48 px tall and fill the width; arrows, Page Up/Down move.
 */
export function DateSheet(props: { open: boolean; value: string; today: string; onPick: (iso: string) => void; onClose: () => void }) {
  const { t, lang } = useT();
  const max = latestDate(props.today);
  const n = names(lang);
  const [view, setView] = useState(() => parseIso(props.value));
  const [focus, setFocus] = useState(props.value);
  const grid = useRef<HTMLDivElement>(null);

  // Each time it opens, start on the picked day's month.
  useEffect(() => {
    if (!props.open) return;
    setView(parseIso(props.value));
    setFocus(props.value);
  }, [props.open, props.value]);

  useEffect(() => {
    if (props.open) grid.current?.querySelector<HTMLButtonElement>(`[data-iso="${focus}"]`)?.focus();
  }, [focus, view, props.open]);

  const moveFocus = (next: string) => {
    if (next > max) next = max;
    setView(parseIso(next));
    setFocus(next);
  };

  function onKey(e: KeyboardEvent) {
    const rtl = document.documentElement.dir === 'rtl' ? -1 : 1;
    const step: Record<string, () => string> = {
      ArrowLeft: () => shiftDays(focus, -1 * rtl),
      ArrowRight: () => shiftDays(focus, rtl),
      ArrowUp: () => shiftDays(focus, -7),
      ArrowDown: () => shiftDays(focus, 7),
      PageUp: () => shiftMonths(focus, -1),
      PageDown: () => shiftMonths(focus, 1),
    };
    const go = step[e.key];
    if (go) {
      e.preventDefault();
      moveFocus(go());
    }
  }

  const shiftView = (by: number) => {
    const first = toIso(view.year, view.month + by, 1);
    setView(parseIso(first));
  };
  const nextDisabled = toIso(view.year, view.month + 1, 1) > max;
  const cells = monthGrid(view.year, view.month);
  const navBtn =
    'flex size-12 items-center justify-center rounded-xl border-[1.5px] border-line-strong bg-paper hover:bg-ground disabled:opacity-30 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink';

  return (
    <Dialog open={props.open} onClose={props.onClose} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-ink/55 transition duration-150 ease-out data-closed:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
        <DialogPanel
          transition
          className="safe-bottom flex max-h-[var(--app-h,100vh)] w-full flex-col gap-3 overflow-y-auto rounded-t-2xl bg-paper px-2 pt-4 pb-3 text-ink shadow-[0_20px_48px_rgba(19,33,31,0.28)] transition duration-150 ease-out data-closed:translate-y-6 data-closed:opacity-0 sm:max-w-md sm:rounded-2xl sm:px-4"
        >
          <div className="flex items-center justify-between gap-2 px-1">
            <DialogTitle className="m-0 text-xl font-bold rtl:text-[22px]">{t('add.pickDate')}</DialogTitle>
            <button
              type="button"
              aria-label={t('add.close')}
              onClick={props.onClose}
              className="flex size-12 items-center justify-center rounded-xl hover:bg-ground focus-visible:outline-3 focus-visible:outline-ink"
            >
              <Icon name="close" size={22} />
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 px-1">
            <button type="button" aria-label={t('add.prevMonth')} onClick={() => shiftView(-1)} className={navBtn}>
              <Icon name="chevronStart" size={22} stroke={2.25} flip />
            </button>
            <span className="num text-[17px] font-bold rtl:text-[19px]" aria-live="polite">
              {n.monthTitle(view.year, view.month)}
            </span>
            <button type="button" aria-label={t('add.nextMonth')} disabled={nextDisabled} onClick={() => shiftView(1)} className={navBtn}>
              <Icon name="chevronEnd" size={22} stroke={2.25} flip />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs font-bold text-muted rtl:text-[11px]" aria-hidden="true">
            {n.weekdays.map((d) => (
              <span key={d} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div
            ref={grid}
            role="group"
            aria-label={n.monthTitle(view.year, view.month)}
            className="grid grid-cols-7 gap-0.5"
            onKeyDown={onKey}
          >
            {cells.map((c) => {
              const selected = c.iso === props.value;
              const isToday = c.iso === props.today;
              const allowed = inRange(c.iso, undefined, max);
              const ahead = c.iso > props.today;
              return (
                <button
                  key={c.iso}
                  type="button"
                  data-iso={c.iso}
                  disabled={!allowed}
                  tabIndex={c.iso === focus ? 0 : -1}
                  aria-label={n.fullDay(c.iso)}
                  aria-pressed={selected}
                  onClick={() => props.onPick(c.iso)}
                  className={`num flex h-12 min-w-0 items-center justify-center rounded-xl text-[17px] focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-ink disabled:cursor-not-allowed disabled:opacity-25 ${
                    selected
                      ? 'bg-ink font-bold text-white'
                      : isToday
                        ? 'font-bold ring-2 ring-ink ring-inset hover:bg-ground'
                        : ahead
                          ? 'border-[1.5px] border-dashed border-line-strong hover:bg-ground'
                          : c.inMonth
                            ? 'hover:bg-ground'
                            : 'text-muted/60 hover:bg-ground'
                  }`}
                >
                  {c.day}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 px-1 pt-1">
            <button
              type="button"
              onClick={() => props.onPick(props.today)}
              className="h-12 rounded-xl border-[1.5px] border-line-strong bg-paper text-base font-bold hover:bg-ground"
            >
              {t('day.today')}
            </button>
            <button type="button" onClick={props.onClose} className="h-12 rounded-xl text-base font-bold text-muted hover:bg-ground">
              {t('app.cancel')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
